import fetchService from '../servises/fetch.service';
import { doSignOut } from '../servises/auth.service';
import { makeAutoObservable } from 'mobx';
import LogRocket from 'logrocket';
import {
    getAccountStateGeneration,
    isAccountStateCurrent,
    resetAccountState
} from '../servises/account-state.service';

class UserStore {
    constructor() {
        makeAutoObservable(this);
    }

    user = null;
    userToken = null;
    logged = null;
    emailVerified = false;
    sub = null;
    apiKey = null;
    authRequestSequence = 0;

    async getCurrentUser(googleJwt) {
        const requestSequence = ++this.authRequestSequence;
        this.userToken = googleJwt;
        let response;
        try {
            response = await fetchService.authenticatedGet(`/user`);
        } catch (error) {
            if (requestSequence !== this.authRequestSequence) {
                return undefined;
            }
            throw error;
        }
        if (requestSequence !== this.authRequestSequence) {
            return undefined;
        }
        resetAccountState();
        this.user = response.data.data_o;
        this.logged = true;
        if (this.user) {
            LogRocket.identify(this.user._id, {
                email: this.user.email
            });
        }
        return this.user;
    }

    async createUser(googleJwt, body) {
        const requestSequence = ++this.authRequestSequence;
        this.userToken = googleJwt;
        let response;
        try {
            response = await fetchService.authenticatedPost(`/user`, body);
        } catch (error) {
            if (requestSequence !== this.authRequestSequence) {
                return undefined;
            }
            throw error;
        }
        if (requestSequence !== this.authRequestSequence) {
            return undefined;
        }
        resetAccountState();
        this.user = response.data.data_o;
        this.logged = true;
        return this.user;
    }

    async getOrCreateUser(googleJwt, body) {
        try {
            const currentUser = await this.getCurrentUser(googleJwt);
            if (currentUser === undefined) {
                return undefined;
            }
            if (currentUser) {
                return currentUser;
            }
        } catch (error) {
            if (error.status !== 401) {
                throw error;
            }
        }
        return this.createUser(googleJwt, body);
    }

    async updateNotifications(key, data, metaData) {
        const accountStateGeneration = getAccountStateGeneration();
        const response = await fetchService.authenticatedPost(
            `/user-notifications`,
            { key, data, metaData }
        );
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.user = response.data.data_o;
    }

    async deleteUser() {
        const accountStateGeneration = getAccountStateGeneration();
        await fetchService.authenticatedDelete(`/user`);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.resetSession();
        await doSignOut();
    }

    async verifyEmail() {
        const accountStateGeneration = getAccountStateGeneration();
        const response = await fetchService.authenticatedGet(`/user-f`);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.user = response.data.data_o;
    }

    async doLogOut(whereTo) {
        this.resetSession();
        await doSignOut();
        if (whereTo) {
            window.location.replace(whereTo);
        } else {
            window.location.reload(true);
        }
    }

    resetSession() {
        this.authRequestSequence += 1;
        resetAccountState();
        this.user = null;
        this.userToken = null;
        this.logged = false;
        this.emailVerified = false;
        this.sub = null;
        this.apiKey = null;
    }

    async getSub() {
        const accountStateGeneration = getAccountStateGeneration();
        const response = await fetchService.authenticatedGet(`/sub`);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.sub = response.data.data_o;
    }

    async generateApiKey() {
        const accountStateGeneration = getAccountStateGeneration();
        const response = await fetchService.authenticatedPost(`/api-key`);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.apiKey = response.data.data_o.apiKey;
    }

    async getApiKey() {
        const accountStateGeneration = getAccountStateGeneration();
        const response = await fetchService.authenticatedGet(`/api-key`);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.apiKey = response.data.data_o.apiKey;
    }

    async deleteApiKey() {
        const accountStateGeneration = getAccountStateGeneration();
        const response = await fetchService.authenticatedDelete(`/api-key`);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.apiKey = response.data.data_o.apiKey;
    }
}

const userStore = new UserStore();

export default userStore;
